import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { DropDTO, DropLayerDTO, LayerDTO } from 'business/models/Models';
import EVolumetricLayerDispense from './EVolumetricLayerDispense';
import EVolumetricLayerType from './EVolumetricLayerType';
import { getLayer } from './GenerateCommon';
import { VolumetricLayerKey } from './VolumetricLayerKey';
import { VolumetricWellDropLayer } from './VolumetricWellDropLayer';

export class VolumetricWellDropLayers {
  private layers: Map<VolumetricLayerKey, VolumetricWellDropLayer> = new Map<
    VolumetricLayerKey,
    VolumetricWellDropLayer
  >();

  public WellDropRow: DropDTO;
  public docData: ExperimentDtoClient;

  constructor(docData: ExperimentDtoClient, wellDropRow: DropDTO) {
    this.WellDropRow = wellDropRow;
    this.docData = docData;

    const manualConcentration = [];
    const robotConcentration = [];

    for (const wellDropLayer of wellDropRow.dropLayerList) {
      const layer: LayerDTO = getLayer(docData, wellDropLayer.layerId);
      if (layer == null) {
        continue;
      }

      if (layer.volumeBasedAddition) {
        this.AddVolumetricLayer(wellDropLayer, layer);
      } else if (layer.dispenseManually) {
        manualConcentration.push(wellDropLayer);
      } else {
        robotConcentration.push(wellDropLayer);
      }
    }

    this.AddConcentrationLayers(EVolumetricLayerDispense.Manual, manualConcentration);
    this.AddConcentrationLayers(EVolumetricLayerDispense.Robot, robotConcentration);
  }

  private AddVolumetricLayer(wellDropLayerRow: DropLayerDTO, lyr: LayerDTO) {
    const dispense: EVolumetricLayerDispense = lyr.dispenseManually
      ? EVolumetricLayerDispense.Manual
      : EVolumetricLayerDispense.Robot;
    const type: EVolumetricLayerType = EVolumetricLayerType.Volumetric;

    const wellDropLayerRows: DropLayerDTO[] = [];

    wellDropLayerRows.push(wellDropLayerRow);
    const layer: VolumetricWellDropLayer = new VolumetricWellDropLayer(
      this.docData,
      this,
      dispense,
      type,
      wellDropLayerRows,
    );
    if (!this.ContainsLayer(layer.Key)) {
      this.layers.set(layer.Key, layer);
    }
  }

  private AddConcentrationLayers(
    dispense: EVolumetricLayerDispense,
    wellDropLayerRows: DropLayerDTO[],
  ) {
    if (wellDropLayerRows.length == 0) {
      return;
    }
    const type: EVolumetricLayerType = EVolumetricLayerType.Concentration;
    const layer: VolumetricWellDropLayer = new VolumetricWellDropLayer(
      this.docData,
      this,
      dispense,
      type,
      wellDropLayerRows,
    );

    this.layers.set(layer.Key, layer);
  }

  public get WellID(): number {
    return this.WellDropRow.dropId;
  }

  public GetLayer(key: VolumetricLayerKey): VolumetricWellDropLayer {
    return this.layers.get(key);
  }

  public ContainsLayer(key: VolumetricLayerKey): boolean {
    return this.layers.has(key);
  }

  public AddLayer(key: VolumetricLayerKey) {
    const wellDropLayerRows = [];
    for (const layerID of key.layerIDs) {
      const layerRow = new LayerDTO();
      if (layerRow == null) {
        layerRow.id = layerID;
        layerRow.volumeBasedAddition = key.Type == EVolumetricLayerType.Volumetric;
        layerRow.dispenseManually = key.Dispense == EVolumetricLayerDispense.Manual;
        layerRow.overrideLayer = false;
        layerRow.locked = false;
        this.docData.layers.push(layerRow);
      }

      const wellDropLayerRow: DropLayerDTO = new DropLayerDTO();
      wellDropLayerRow.id = this.WellDropRow.dropId;
      wellDropLayerRow.layerId = layerRow.id;
      wellDropLayerRow.volume = 0;
      wellDropLayerRow.waterVolume = 0;

      wellDropLayerRows.push(wellDropLayerRow);
    }

    const layer: VolumetricWellDropLayer = new VolumetricWellDropLayer(
      this.docData,
      this,
      key.Dispense,
      key.Type,
      wellDropLayerRows,
    );
    this.layers.set(layer.Key, layer);
  }

  public IsSingleLayer(): boolean {
    return this.layers.size == 1;
  }

  public get SingleLayer(): VolumetricWellDropLayer {
    return this.GetLayerByIndex(0);
  }

  public GetLayerByIndex(index: number): VolumetricWellDropLayer {
    return this.layers[this.layers.keys[index]];
  }

  public get Count(): number {
    return this.layers.size;
  }

  public static IsSingleLayer(docData, wellDropRow): boolean {
    return new VolumetricWellDropLayers(docData, wellDropRow).IsSingleLayer();
  }

  public GetEnumerator() {
    return this.layers;
  }

  private get RobotLayers(): VolumetricWellDropLayers {
    const robotLayers: VolumetricWellDropLayers = new VolumetricWellDropLayers(
      this.docData,
      this.WellDropRow,
    );

    const manualKeys: VolumetricLayerKey[] = new Array<VolumetricLayerKey>();

    for (const key of robotLayers.layers.keys()) {
      if (key.Dispense == EVolumetricLayerDispense.Manual) {
        manualKeys.push(key);
      }
    }

    for (const key of manualKeys) {
      robotLayers.layers.delete(key);
    }

    return robotLayers;
  }

  public static GetWellDropLayers(docData, wellDropRow): VolumetricWellDropLayers {
    return new VolumetricWellDropLayers(docData, wellDropRow);
  }

  public static GetRobotLayers(docData, wellDropRow): VolumetricWellDropLayers {
    return new VolumetricWellDropLayers(docData, wellDropRow).RobotLayers;
  }
}
